/**
 * EvidencePlanDetailModal — Displays full detail for a single evidence plan item.
 *
 * Opens when user clicks a row in the Evidence Collection Plan table.
 * Shows title, type, priority, effort, description, collection guidance,
 * hypothesis link, and online source with link-health controls.
 *
 * Users can flag source links as broken; a Wayback Machine fallback is
 * automatically offered when a link is marked broken.
 */

import React, { useState } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import CloseButton from "../../Components/CloseButton/CloseButton";
import styles from "./EvidencePlanDetailModal.module.css";

export interface EvidencePlanItem {
  title: string;
  evidence_type: string;
  description?: string;
  collection_guidance?: string;
  priority?: number;
  hypothesis_link?: string;
  estimated_effort?: string;
  source_url?: string;
  source_name?: string;
}

export type LinkStatus = "unknown" | "active" | "broken" | "archived";

interface Props {
  item: EvidencePlanItem;
  onClose: () => void;
  /** Optional: called when user flags a link status (for persisting to DB on evidence items) */
  onLinkStatusChange?: (sourceUrl: string, status: LinkStatus) => void;
  /** Optional: pre-set link status from parent (e.g. from DB evidence item) */
  linkStatus?: LinkStatus;
}

const PRIORITY_LABELS: Record<number, string> = {
  1: "High",
  2: "Med\u2013High",
  3: "Medium",
  4: "Low\u2013Med",
  5: "Low",
};

const EFFORT_DISPLAY: Record<string, { icon: string; label: string }> = {
  quick: { icon: "\u26A1", label: "Quick (1\u20133 days)" },
  moderate: { icon: "\u23F1", label: "Moderate (1\u20132 weeks)" },
  deep: { icon: "\uD83D\uDD2C", label: "Deep (2+ weeks)" },
};

/**
 * Sanitize AI-generated URLs:
 *  - Strip markdown link syntax: [label](url) → url
 *  - Ensure absolute: add https:// if missing protocol
 *  - Trim whitespace
 */
function sanitizeSourceUrl(raw: string): string {
  let url = raw.trim();

  // Handle markdown-style links: [label](url) or bare [url](url)
  const mdMatch = url.match(/\[.*?\]\((https?:\/\/[^)]+)\)/);
  if (mdMatch) return mdMatch[1];

  // Handle bracket-wrapped: [https://example.com]
  const bracketMatch = url.match(/^\[(https?:\/\/[^\]]+)\]$/);
  if (bracketMatch) return bracketMatch[1];

  // If no protocol, add https://
  if (url && !url.startsWith("http://") && !url.startsWith("https://")) {
    url = `https://${url}`;
  }

  return url;
}

/** Build a Wayback Machine lookup URL for a given source URL */
function waybackUrl(sourceUrl: string): string {
  const clean = sanitizeSourceUrl(sourceUrl);
  return `https://web.archive.org/web/*/${encodeURIComponent(clean)}`;
}

const EvidencePlanDetailModal: React.FC<Props> = ({
  item,
  onClose,
  onLinkStatusChange,
  linkStatus: externalStatus,
}) => {
  const { t } = useAppTranslation("copilot");
  const [localStatus, setLocalStatus] = useState<LinkStatus>(externalStatus ?? "unknown");

  const currentStatus = externalStatus ?? localStatus;

  const effort = item.estimated_effort
    ? EFFORT_DISPLAY[item.estimated_effort] ?? { icon: "", label: item.estimated_effort }
    : null;

  const priorityText = item.priority ? PRIORITY_LABELS[item.priority] ?? `P${item.priority}` : null;

  // Parse collection_guidance into steps.
  // Try newline split first; if that yields a single chunk the AI likely
  // returned all steps on one line ("1. Foo 2. Bar 3. Baz"), so fall back
  // to splitting on the "N. " numbering pattern.
  const guidanceSteps = (() => {
    if (!item.collection_guidance) return [];
    let raw = item.collection_guidance.split("\n").filter((s) => s.trim());
    if (raw.length <= 1) {
      raw = item.collection_guidance
        .split(/(?=\d+\.\s)/)
        .filter((s) => s.trim());
    }
    return raw
      .map((s) => s.replace(/^\d+[.)]\s*/, "").trim())
      .filter(Boolean);
  })();

  const handleStatusToggle = (newStatus: LinkStatus) => {
    setLocalStatus(newStatus);
    if (onLinkStatusChange && item.source_url) {
      onLinkStatusChange(sanitizeSourceUrl(item.source_url), newStatus);
    }
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div
        className={styles.modal}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={item.title}
      >
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <span className={styles.typeBadge}>
              {t(`evidenceTypes.${item.evidence_type}`) || item.evidence_type}
            </span>
            {priorityText && (
              <span className={styles.priorityBadge}>
                {t("plan.colPriority")}: {priorityText}
              </span>
            )}
            {effort && (
              <span className={styles.effortBadge}>
                {effort.icon} {effort.label}
              </span>
            )}
          </div>
          <CloseButton onClick={onClose} size="md" ariaLabel={t("plan.modalClose")} />
        </div>

        {/* Title */}
        <h3 className={styles.title}>{item.title}</h3>

        {/* Description */}
        {item.description && (
          <div className={styles.section}>
            <h4 className={styles.sectionLabel}>{t("plan.modalDescription")}</h4>
            <p className={styles.sectionText}>{item.description}</p>
          </div>
        )}

        {/* Collection Guidance */}
        {guidanceSteps.length > 0 && (
          <div className={styles.section}>
            <h4 className={styles.sectionLabel}>{t("plan.modalGuidance")}</h4>
            <ol className={styles.guidanceList}>
              {guidanceSteps.map((step, i) => {
                // Strip leading "1. " or "1) " numbering if present
                const cleaned = step.replace(/^\d+[.)]\s*/, "").trim();
                return cleaned ? (
                  <li key={i} className={styles.guidanceStep}>{cleaned}</li>
                ) : null;
              })}
            </ol>
          </div>
        )}

        {/* Hypothesis Link */}
        {item.hypothesis_link && (
          <div className={styles.section}>
            <h4 className={styles.sectionLabel}>{t("plan.modalHypothesis")}</h4>
            <p className={styles.sectionText}>{item.hypothesis_link}</p>
          </div>
        )}

        {/* Online Source */}
        {item.source_url && (
          <div className={`${styles.sourceSection} ${currentStatus === "broken" ? styles.sourceBroken : ""}`}>
            <h4 className={styles.sectionLabel}>
              {/* External link icon */}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: "-2px", marginInlineEnd: "6px" }}>
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                <polyline points="15 3 21 3 21 9" />
                <line x1="10" y1="14" x2="21" y2="3" />
              </svg>
              {t("plan.modalSource")}
              {/* Status indicator */}
              {currentStatus === "broken" && (
                <span className={styles.linkStatusBadge} data-status="broken">
                  {t("plan.linkBroken")}
                </span>
              )}
              {currentStatus === "active" && (
                <span className={styles.linkStatusBadge} data-status="active">
                  {t("plan.linkActive")}
                </span>
              )}
            </h4>

            {/* Source link */}
            <a
              href={sanitizeSourceUrl(item.source_url)}
              target="_blank"
              rel="noopener noreferrer"
              className={`${styles.sourceLink} ${currentStatus === "broken" ? styles.sourceLinkBroken : ""}`}
            >
              {item.source_name || item.source_url}
            </a>

            {/* Link health actions */}
            <div className={styles.linkActions}>
              {currentStatus !== "broken" ? (
                <button
                  type="button"
                  className={styles.linkFlagBtn}
                  onClick={() => handleStatusToggle("broken")}
                  title={t("plan.linkFlagBrokenTitle")}
                >
                  {/* Flag icon */}
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: "-1px", marginInlineEnd: "4px" }}>
                    <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
                    <line x1="4" y1="22" x2="4" y2="15" />
                  </svg>
                  {t("plan.linkFlagBroken")}
                </button>
              ) : (
                <button
                  type="button"
                  className={styles.linkUnflagBtn}
                  onClick={() => handleStatusToggle("unknown")}
                >
                  {t("plan.linkClearFlag")}
                </button>
              )}

              {currentStatus === "active" ? null : (
                <button
                  type="button"
                  className={styles.linkConfirmBtn}
                  onClick={() => handleStatusToggle("active")}
                  title={t("plan.linkConfirmActiveTitle")}
                >
                  {t("plan.linkConfirmActive")}
                </button>
              )}
            </div>

            {/* Wayback Machine fallback — shown when flagged broken */}
            {currentStatus === "broken" && (
              <div className={styles.waybackSection}>
                <span className={styles.waybackLabel}>
                  {t("plan.waybackHint")}
                </span>
                <a
                  href={waybackUrl(item.source_url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.waybackLink}
                >
                  {t("plan.waybackSearch")}
                </a>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EvidencePlanDetailModal;
